import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, ExternalLink } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

/**
 * MiniAudioPlayer - A tiny, non-invasive audio player for Cafe Hot Seat posts
 * 
 * Supports:
 * - Direct audio files (uploaded tracks)
 * - Spotify embeds (using Spotify iframe embed)
 * - SoundCloud embeds
 * - YouTube embeds
 * - Fallback to external link
 * 
 * This component is ISOLATED from the main session player.
 * Does NOT share state with UnifiedMusicPlayer or any other player.
 */
const MiniAudioPlayer = ({ 
  audioUrl, 
  streamingLink, 
  trackTitle,
  artistName 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  // Detect platform from streaming link
  const detectPlatform = (url) => {
    if (!url) return null;
    if (url.includes('spotify.com')) return 'spotify';
    if (url.includes('soundcloud.com')) return 'soundcloud';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('apple.com')) return 'apple';
    return 'other';
  };

  const platform = detectPlatform(streamingLink);

  // Extract Spotify track ID
  const getSpotifyTrackId = (url) => {
    const match = url?.match(/track\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  // Extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  // Handle direct audio play/pause with proper error handling
  const toggleAudio = async () => {
    if (!audioRef.current) return;
    
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Reset audio error state
        setAudioError(false);
        // Load the audio if not loaded
        if (audioRef.current.readyState === 0) {
          audioRef.current.load();
        }
        // Play returns a promise - handle it properly
        await audioRef.current.play();
        setIsPlaying(true);
        
        // Track café song play for analytics
        trackEvent('cafe_play', {
          track_title: trackTitle,
          artist_name: artistName,
          source: 'direct_audio'
        });
      }
    } catch (error) {
      console.error('Audio playback error:', error);
      setAudioError(true);
      setIsPlaying(false);
    }
  };

  // Handle audio errors
  const handleAudioError = () => {
    console.error('Audio failed to load');
    setAudioError(true);
    setIsPlaying(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
      }
    };
  }, []);

  // Common button styles for congruent appearance
  const buttonBaseClass = "w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors";

  // If we have a direct audio URL, use native audio player
  if (audioUrl) {
    return (
      <div>
        <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
          <button
            onClick={toggleAudio}
            disabled={audioError}
            className={`w-10 h-10 flex items-center justify-center rounded-full text-white transition-colors flex-shrink-0 ${
              audioError 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-black hover:bg-gray-800'
            }`}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{trackTitle}</p>
            <p className="text-xs text-gray-500 truncate">{artistName}</p>
            {audioError && (
              <p className="text-xs text-red-500 mt-1">Unable to play audio</p>
            )}
          </div>
          <Volume2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => setIsPlaying(false)}
          onError={handleAudioError}
          preload="metadata"
        />
      </div>
    );
  }

  // Spotify embed - mobile-optimized with full width
  if (platform === 'spotify') {
    const trackId = getSpotifyTrackId(streamingLink);
    if (trackId) {
      return (
        <div>
          {!showEmbed ? (
            <button
              onClick={() => {
                setShowEmbed(true);
                trackEvent('cafe_play', {
                  track_title: trackTitle,
                  artist_name: artistName,
                  source: 'spotify'
                });
              }}
              className={`${buttonBaseClass} bg-[#1DB954] text-white hover:bg-[#1ed760]`}
            >
              <Play className="w-5 h-5" />
              Play on Spotify
            </button>
          ) : (
            <div className="rounded-lg overflow-hidden bg-black">
              <iframe
                src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
                width="100%"
                height="152"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-lg"
                style={{ minHeight: '152px' }}
              />
            </div>
          )}
        </div>
      );
    }
  }

  // YouTube embed
  if (platform === 'youtube') {
    const videoId = getYouTubeVideoId(streamingLink);
    if (videoId) {
      return (
        <div>
          {!showEmbed ? (
            <button
              onClick={() => {
                setShowEmbed(true);
                trackEvent('cafe_play', {
                  track_title: trackTitle,
                  artist_name: artistName,
                  source: 'youtube'
                });
              }}
              className={`${buttonBaseClass} bg-[#FF0000] text-white hover:bg-[#cc0000]`}
            >
              <Play className="w-5 h-5" />
              Play on YouTube
            </button>
          ) : (
            <div className="rounded-lg overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1`}
                width="100%"
                height="180"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              />
            </div>
          )}
        </div>
      );
    }
  }

  // SoundCloud embed
  if (platform === 'soundcloud') {
    return (
      <div>
        {!showEmbed ? (
          <button
            onClick={() => {
              setShowEmbed(true);
              trackEvent('cafe_play', {
                track_title: trackTitle,
                artist_name: artistName,
                source: 'soundcloud'
              });
            }}
            className={`${buttonBaseClass} bg-[#FF5500] text-white hover:bg-[#ff6a1a]`}
          >
            <Play className="w-5 h-5" />
            Play on SoundCloud
          </button>
        ) : (
          <div className="rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="166"
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
              src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(streamingLink)}&color=%23ff5500&auto_play=true&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
            />
          </div>
        )}
      </div>
    );
  }

  // Fallback: External link button for other platforms
  if (streamingLink) {
    return (
      <div>
        <a
          href={streamingLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent('cafe_play', {
              track_title: trackTitle,
              artist_name: artistName,
              source: platform || 'external'
            });
          }}
          className={`${buttonBaseClass} bg-gray-800 text-white hover:bg-gray-700`}
        >
          <Play className="w-5 h-5" />
          Listen to Track
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    );
  }

  return null;
};

export default MiniAudioPlayer;
